import { useEffect, useRef, useState } from "react";
import { authApi, api, formatApiErrorDetail } from "@/lib/api";
import { DEFAULT_CONTENT, resolveImg } from "@/lib/content";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Upload } from "lucide-react";

const field =
  "bg-transparent border-0 border-b border-[#8E1226]/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#0E6B3A]";

function Section({ title, children }) {
  return (
    <div className="rounded-2xl border border-[#8E1226]/12 p-6 md:p-8 mb-6">
      <h3 className="font-serif text-2xl text-[#8E1226] mb-6">{title}</h3>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function Text({ label, value, onChange, textarea, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#8E1226] text-sm">{label}</Label>
      {textarea ? (
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          rows={3}
          className="bg-transparent border border-[#8E1226]/20 rounded-lg focus-visible:ring-0 focus-visible:border-[#0E6B3A] resize-none"
        />
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={field} />
      )}
    </div>
  );
}

function ImageField({ label, value, onChange, token }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authApi(token).post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-[#8E1226] text-sm">{label}</Label>
      <div className="flex items-start gap-4">
        <div className="w-32 h-24 rounded-lg overflow-hidden border border-[#8E1226]/12 bg-[#F2E8D5] shrink-0">
          {value ? (
            <img src={resolveImg(value)} alt="" className="w-full h-full object-cover" />
          ) : null}
        </div>
        <div className="flex-1 space-y-2">
          <input ref={inputRef} type="file" accept="image/*" onChange={upload} className="hidden" />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-full border border-[#8E1226]/20 px-4 py-2 text-sm hover:border-[#0E6B3A] hover:text-[#0E6B3A] transition-colors"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload image
          </button>
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="…or paste an image URL"
            className={`${field} text-sm`}
          />
        </div>
      </div>
    </div>
  );
}

function VideoField({ label, value, onChange, token }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authApi(token).post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Video uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-[#8E1226] text-sm">{label}</Label>
      <div className="flex items-start gap-4">
        <div className="w-24 rounded-lg overflow-hidden border border-[#8E1226]/12 bg-[#F2E8D5] shrink-0">
          {value ? (
            <video src={resolveImg(value)} muted loop playsInline autoPlay className="w-full h-32 object-cover" />
          ) : null}
        </div>
        <div className="flex-1 space-y-2">
          <input ref={inputRef} type="file" accept="video/*" onChange={upload} className="hidden" />
          <button
            type="button"
            data-testid="content-video-upload-btn"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-full border border-[#8E1226]/20 px-4 py-2 text-sm hover:border-[#0E6B3A] hover:text-[#0E6B3A] transition-colors"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload video
          </button>
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="…or paste a video URL (leave empty to hide the section)"
            data-testid="content-video-url"
            className={`${field} text-sm`}
          />
        </div>
      </div>
    </div>
  );
}

export default function ContentPanel({ token }) {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/content")
      .then(({ data }) => setC({ ...DEFAULT_CONTENT, ...data }))
      .catch(() => setC(DEFAULT_CONTENT));
  }, []);

  const set = (k, v) => setC((prev) => ({ ...prev, [k]: v }));
  const setChapter = (i, k, v) =>
    setC((prev) => {
      const chapters = [...(prev.chapters || [])];
      chapters[i] = { ...chapters[i], [k]: v };
      return { ...prev, chapters };
    });
  const setStep = (i, k, v) =>
    setC((prev) => {
      const howto_steps = [...(prev.howto_steps || [])];
      howto_steps[i] = { ...howto_steps[i], [k]: v };
      return { ...prev, howto_steps };
    });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...c,
        ribbon_words:
          typeof c.ribbon_words === "string"
            ? c.ribbon_words.split(",").map((s) => s.trim()).filter(Boolean)
            : c.ribbon_words,
      };
      await authApi(token).put("/admin/content", payload);
      toast.success("Page content saved. Refresh the site to see it live.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!c) {
    return (
      <div className="flex items-center gap-3 text-[#6D726A] py-10">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading content…
      </div>
    );
  }

  const ribbonStr = Array.isArray(c.ribbon_words) ? c.ribbon_words.join(", ") : c.ribbon_words;

  return (
    <div data-testid="admin-content-panel" className="max-w-3xl">
      <Section title="Branding">
        <Text label="Brand name (line 1)" value={c.brand_name_1} onChange={(v) => set("brand_name_1", v)} />
        <Text label="Brand name (line 2 / location)" value={c.brand_name_2} onChange={(v) => set("brand_name_2", v)} />
        <ImageField label="Logo" value={c.logo_image} onChange={(v) => set("logo_image", v)} token={token} />
      </Section>

      <Section title="Hero">
        <Text label="Eyebrow (small line above heading)" value={c.hero_eyebrow} onChange={(v) => set("hero_eyebrow", v)} testId="content-hero-eyebrow" />
        <Text label="Headline line 1" value={c.hero_line_1} onChange={(v) => set("hero_line_1", v)} />
        <Text label="Headline line 2 (highlighted word)" value={c.hero_line_2} onChange={(v) => set("hero_line_2", v)} />
        <Text label="Headline line 3" value={c.hero_line_3} onChange={(v) => set("hero_line_3", v)} />
        <Text label="Paragraph" value={c.hero_paragraph} onChange={(v) => set("hero_paragraph", v)} textarea />
        <Text label="Button label" value={c.hero_cta} onChange={(v) => set("hero_cta", v)} />
        <Text label="Small note under button" value={c.hero_note} onChange={(v) => set("hero_note", v)} />
        <ImageField label="Hero image" value={c.hero_image} onChange={(v) => set("hero_image", v)} token={token} />
      </Section>

      <Section title="Scrolling ribbon">
        <Text label="Words (comma-separated)" value={ribbonStr} onChange={(v) => set("ribbon_words", v)} />
      </Section>

      <Section title="Why volunteer (manifesto)">
        <Text label="Eyebrow" value={c.manifesto_eyebrow} onChange={(v) => set("manifesto_eyebrow", v)} />
        <Text label="Section heading" value={c.manifesto_title} onChange={(v) => set("manifesto_title", v)} textarea />
        {(c.chapters || []).map((ch, i) => (
          <div key={i} className="rounded-xl border border-[#8E1226]/10 p-5 space-y-4">
            <div className="text-xs uppercase tracking-[0.18em] text-[#0E6B3A]">Chapter {String(i + 1).padStart(2, "0")}</div>
            <Text label="Title" value={ch.title} onChange={(v) => setChapter(i, "title", v)} />
            <Text label="Body" value={ch.body} onChange={(v) => setChapter(i, "body", v)} textarea />
            <ImageField label="Image" value={ch.img} onChange={(v) => setChapter(i, "img", v)} token={token} />
          </div>
        ))}
      </Section>

      <Section title="Video section">
        <Text label="Eyebrow" value={c.video_eyebrow} onChange={(v) => set("video_eyebrow", v)} />
        <Text label="Heading" value={c.video_title} onChange={(v) => set("video_title", v)} />
        <Text label="Paragraph" value={c.video_paragraph} onChange={(v) => set("video_paragraph", v)} textarea />
        <VideoField label="Video" value={c.video_url} onChange={(v) => set("video_url", v)} token={token} />
      </Section>

      <Section title="How to sign up (steps)">
        <Text label="Eyebrow" value={c.howto_eyebrow} onChange={(v) => set("howto_eyebrow", v)} />
        <Text label="Section heading" value={c.howto_title} onChange={(v) => set("howto_title", v)} />
        {(c.howto_steps || []).map((step, i) => (
          <div key={i} className="rounded-xl border border-[#8E1226]/10 p-5 space-y-4">
            <div className="text-xs uppercase tracking-[0.18em] text-[#0E6B3A]">Step {i + 1}</div>
            <Text label="Title" value={step.title} onChange={(v) => setStep(i, "title", v)} testId={`howto-step-${i + 1}-title`} />
            <Text label="Body" value={step.body} onChange={(v) => setStep(i, "body", v)} textarea />
          </div>
        ))}
        <Text label="Phone note (shown next to your phone number)" value={c.howto_note} onChange={(v) => set("howto_note", v)} textarea />
        <Text label="'On the day' panel title" value={c.onday_title} onChange={(v) => set("onday_title", v)} testId="content-onday-title" />
        <Text label="'On the day' panel text (your office address is shown under it automatically)" value={c.onday_body} onChange={(v) => set("onday_body", v)} textarea />
      </Section>

      <Section title="Sign-up section">
        <Text label="Eyebrow" value={c.signup_eyebrow} onChange={(v) => set("signup_eyebrow", v)} />
        <Text label="Heading" value={c.signup_title} onChange={(v) => set("signup_title", v)} />
        <Text label="Paragraph" value={c.signup_paragraph} onChange={(v) => set("signup_paragraph", v)} textarea />
        <Text label="'Click a time' instruction (bold line under the day tabs)" value={c.signup_instruction} onChange={(v) => set("signup_instruction", v)} testId="content-signup-instruction" />
      </Section>

      <Section title="Footer & contact details">
        <Text label="Eyebrow" value={c.footer_eyebrow} onChange={(v) => set("footer_eyebrow", v)} />
        <Text label="Prompt" value={c.footer_prompt} onChange={(v) => set("footer_prompt", v)} textarea />
        <Text label="Phone" value={c.contact_phone} onChange={(v) => set("contact_phone", v)} />
        <Text label="Email" value={c.contact_email} onChange={(v) => set("contact_email", v)} />
        <Text label="Address" value={c.contact_address} onChange={(v) => set("contact_address", v)} textarea />
        <Text label="Charity number" value={c.charity_no} onChange={(v) => set("charity_no", v)} />
        <Text label="Large footer text" value={c.footer_big_text} onChange={(v) => set("footer_big_text", v)} />
      </Section>

      <div className="sticky bottom-6 flex justify-end">
        <button
          data-testid="content-save-btn"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-[#0E6B3A] text-[#FBF7F0] px-8 py-4 text-sm font-medium shadow-lg hover:bg-[#083D21] transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save all changes
        </button>
      </div>
    </div>
  );
}
